import { useTranslation } from 'react-i18next';

import { ProjectDtoIn } from '@/models/in/ProjectDtoIn';
import { ProjectStatus, isPendingProjectStatus } from '@/models/common/ProjectStatus';

interface ConversationItem {
  kind: 'question' | 'answer';
  text: string;
  at: string;
}

interface ProjectStatusTimelineProps {
  project: ProjectDtoIn;
}

const formatDate = (value?: string | null): string =>
  value
    ? Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value))
    : '';

/**
 * "סטטוס לפי קו זמן" — the designer's three-stage timeline, where the middle stage
 * ("הפרויקט ממתין לאישור") holds the three review sub-stages as nested rows.
 *
 * A stage is "done" when statusHistory records it, "current" when it is the project's status
 * now, and dimmed otherwise — so the timeline is driven entirely by the server's append-only
 * history and never guesses.
 */
const ProjectStatusTimeline = ({ project }: ProjectStatusTimelineProps) => {
  const { t } = useTranslation();

  const historyByStatus = new Map(project.statusHistory.map((entry) => [entry.status, entry]));
  const reachedStatus = (status: ProjectStatus) => historyByStatus.has(status);

  const subStages: ProjectStatus[] = [
    ProjectStatus.PendingInitialReview,
    ProjectStatus.PendingWelfareCommittee,
    ProjectStatus.PendingSupportsCommittee,
    // Not every project is referred to the legal department, so it is not drawn as a stage
    // still ahead of everyone - only on the projects that went there.
    ...(reachedStatus(ProjectStatus.PendingLegalReview) || project.status === ProjectStatus.PendingLegalReview
      ? [ProjectStatus.PendingLegalReview]
      : []),
  ];

  const isRejected = project.status === ProjectStatus.Rejected;
  const isWaitingForCoordinator = project.status === ProjectStatus.PendingCoordinatorResponse;
  // Questions and answers in order (SPR-10302): a question is the comment of a
  // PendingCoordinatorResponse entry, an answer is the entry carrying coordinatorResponse.
  const conversation = project.statusHistory.flatMap((entry): ConversationItem[] => {
    if (entry.status === ProjectStatus.PendingCoordinatorResponse && entry.comment) {
      return [{ kind: 'question', text: entry.comment, at: entry.at }];
    }
    if (entry.coordinatorResponse) {
      return [{ kind: 'answer', text: entry.coordinatorResponse, at: entry.at }];
    }
    return [];
  });
  const isApproved = project.status === ProjectStatus.Approved;
  // The submission stage is the project's creation, which always happened.
  const submittedAt = project.statusHistory[0]?.at ?? project.createdAt;

  const Dot = ({ active }: { active: boolean }) => (
    <span
      className={`w-[10px] h-[10px] rounded-full shrink-0 ${active ? 'bg-(--surface-primary)' : 'bg-(--border-main)'}`}
    />
  );

  return (
    <section className="flex flex-col gap-4">
      <h4 className="body-lr-semibold text-(--font-primary)">{t('projects.blade.timelineTitle')}</h4>

      <ol className="flex flex-col gap-4">
        <li className="flex items-center gap-3">
          <Dot active />
          <span className="body-md-regular">{t('projects.blade.timeline.submitted')}</span>
          <span className="body-sm-regular text-(--font-secondary)">{formatDate(submittedAt)}</span>
        </li>

        <li className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Dot active={isPendingProjectStatus(project.status) || isWaitingForCoordinator || isApproved} />
            <span className="body-md-regular">{t('projects.blade.timeline.pending')}</span>
          </div>
          <ol className="flex flex-col gap-2 ms-6 ps-4 border-s border-(--border-main)">
            {subStages.map((stage) => {
              const entry = historyByStatus.get(stage);
              const isCurrent = project.status === stage;
              const isDone = reachedStatus(stage);
              return (
                <li
                  key={stage}
                  className={`flex items-center gap-3 ${isDone || isCurrent ? '' : 'opacity-50'}`}
                >
                  <Dot active={isDone || isCurrent} />
                  <span className={`body-sm-regular ${isCurrent ? 'font-semibold' : ''}`}>
                    {t(`projects.status.${stage}`)}
                  </span>
                  {entry && (
                    <span className="body-xs-regular text-(--font-secondary)">{formatDate(entry.at)}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </li>

        <li className={`flex items-center gap-3 ${isApproved ? '' : 'opacity-50'}`}>
          <Dot active={isApproved} />
          <span className="body-md-regular">{t('projects.blade.timeline.approved')}</span>
          {isApproved && (
            <span className="body-sm-regular text-(--font-secondary)">
              {formatDate(historyByStatus.get(ProjectStatus.Approved)?.at)}
            </span>
          )}
        </li>

        {/* A rejection is not one of the designer's three stages, so it is shown as its own
            terminal row rather than pretending the approval stage was reached. */}
        {isRejected && (
          <li className="flex items-center gap-3">
            <span className="w-[10px] h-[10px] rounded-full shrink-0 bg-(--icon-status-4)" />
            <span className="body-md-regular">{t('projects.status.Rejected')}</span>
            <span className="body-sm-regular text-(--font-secondary)">
              {formatDate(historyByStatus.get(ProjectStatus.Rejected)?.at)}
            </span>
          </li>
        )}
      </ol>

      {conversation.length > 0 && (
        <div className="flex flex-col gap-2">
          <h5 className="body-md-semibold text-(--font-primary)">{t('projects.blade.conversationTitle')}</h5>
          <ol className="flex flex-col gap-2">
            {conversation.map((item, index) => (
              <li
                key={`${item.kind}-${item.at}-${index}`}
                className={`rounded-lg px-3 py-2 ${item.kind === 'question' ? 'bg-(--surface-secondary)' : 'border border-(--border-main)'}`}
              >
                <span className="body-xs-semibold text-(--font-secondary)">
                  {t(item.kind === 'question' ? 'projects.blade.questionFromOffice' : 'projects.blade.coordinatorAnswer')}
                  {' · '}
                  {formatDate(item.at)}
                </span>
                <p className="body-sm-regular whitespace-pre-line">{item.text}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* While a question is open the comment IS that question, already shown above. */}
      {project.statusComment && !isWaitingForCoordinator && (
        <p className="body-sm-regular text-(--font-secondary) bg-(--surface-secondary) rounded-lg px-3 py-2">
          {project.statusComment}
        </p>
      )}
    </section>
  );
};

export default ProjectStatusTimeline;
